/** Product family wrapper — attaches variant browse controls to the main card shell. */

import { h } from 'preact';
import { useState, useEffect, useMemo, useRef } from 'preact/hooks';
import { ResultCard } from './ResultCard';
import { fetchFamily, formatPrice, trackClick } from '../api';
import type { ProductResult, FamilyChild, FamilySelectorColumn } from '../api';
import { ColumnHeader } from './familyTable/ColumnHeader';
import {
  COL_PARTNUM,
  COL_PRICE,
  COL_SHIPS,
  cleanAttribute,
  compareSortKeys,
  formatCertifications,
  formatLeadTime,
  getCellSortKey,
  getCellValue,
  getColumnAlignment,
  getColumnWidthClass,
  getDistinctValues,
  isChildReadyNow,
  minWidthForColumn,
} from './familyTable/model';
import type { ColumnAlign, ColumnDef, FilterOptionsByKey, FilterState, SortState } from './familyTable/model';

interface FamilyStackProps {
  product: ProductResult;
  query: string;
  position: number;
  searchEventId?: string;
}

export function FamilyStack({
  product,
  query,
  position,
  searchEventId,
}: FamilyStackProps) {
  // Model selector is collapsed by default. Earlier iteration auto-expanded per a
  // stakeholder ask, but customer feedback (May 6) was that the expanded
  // selector pulls attention away from the actual product card and the
  // saturated blue strip reads as a CTA rather than a secondary affordance.
  // Default-collapsed restores the "See N variants" tab as the primary
  // affordance, and customers who want the variant list click in.
  const [expanded, setExpanded] = useState(false);
  const [children, setChildren] = useState<FamilyChild[] | null>(null);
  const [selectorColumns, setSelectorColumns] = useState<FamilySelectorColumn[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalChildren, setTotalChildren] = useState(product.sibling_count ?? 0);
  const [sort, setSort] = useState<SortState | null>(null);
  const [filters, setFilters] = useState<FilterState>({});
  const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);
  const [filterPopoverStyle, setFilterPopoverStyle] = useState<h.JSX.CSSProperties | undefined>(undefined);
  const variantsRef = useRef<HTMLDivElement>(null);
  const filterPopoverRef = useRef<HTMLDivElement>(null);
  const filterAnchorRef = useRef<HTMLElement | null>(null);
  const suppressTableClickUntilRef = useRef(0);

  // Parent rows have parent_product_id=null but is_parent_product=true; their
  // own product_id IS the family anchor. Without this fallback the sibling
  // fetch short-circuits and parent cards expand to "No other products".
  const parentId = product.parent_product_id
    ?? (product.is_parent_product ? product.product_id : null);

  const isParentRow = !!product.is_parent_product;
  const variantCount = isParentRow
    ? totalChildren
    : Math.max(totalChildren - 1, 0);

  // Eager family prefetch for all parent rows so parent-card stock state and
  // variant count settle before the user expands the model table.
  const needsFamilyPrefetch = isParentRow && parentId != null;
  const [familyPrefetched, setFamilyPrefetched] = useState(!needsFamilyPrefetch);

  useEffect(() => {
    if (!needsFamilyPrefetch || familyPrefetched || children !== null) return;
    const controller = new AbortController();
    const delay = Math.min(position * 35, 350);
    const timer = setTimeout(() => {
      fetchFamily(parentId!, controller.signal)
      .then((resp) => {
        const siblings = resp.children.filter((c) => c.product_id !== product.product_id);
        setChildren(siblings);
        setSelectorColumns(resp.selector_columns || []);
        setTotalChildren(resp.total_children);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          console.error('[TerraSearch] Family count discovery failed:', err);
          setChildren([]);
        }
      })
      .finally(() => setFamilyPrefetched(true));
    }, delay);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [needsFamilyPrefetch, familyPrefetched, children, parentId, product.product_id, position]);

  useEffect(() => {
    if (!expanded || children !== null || !parentId) return;
    const controller = new AbortController();
    setLoading(true);
    const delay = Math.min(position * 50, 500);
    const timer = setTimeout(() => {
      fetchFamily(parentId, controller.signal)
        .then((resp) => {
          const siblings = resp.children.filter((c) => c.product_id !== product.product_id);
          setChildren(siblings);
          setSelectorColumns(resp.selector_columns || []);
          setTotalChildren(resp.total_children);
        })
        .catch((err) => {
          if (err.name !== 'AbortError') {
            console.error('[TerraSearch] Family fetch failed:', err);
            setChildren([]);
          }
        })
        .finally(() => setLoading(false));
    }, delay);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [expanded, children, parentId, product.product_id, query, position]);

  const rawChildren = children || [];
  const hasSelectorColumns = selectorColumns.length > 0;

  // Compose columns once per render. Terra Part #, Price, and Ships in
  // form the buying triad; A4 selector attributes then preserve the live
  // parent-page order. Legacy fallback still renders a complete table when
  // /family doesn't return selector_columns.
  const columns = useMemo<ColumnDef[]>(() => {
    const partnum: ColumnDef = { key: COL_PARTNUM, label: 'Terra Part #', sortable: true, filterable: false };
    const price: ColumnDef = { key: COL_PRICE, label: 'Price', sortable: true, filterable: false };
    const ships: ColumnDef = { key: COL_SHIPS, label: 'Ships in', sortable: true, filterable: true };
    if (hasSelectorColumns) {
      return [
        partnum,
        price,
        ships,
        ...selectorColumns.map<ColumnDef>((c) => ({
          key: c.key,
          label: c.label,
          sortable: true,
          filterable: true,
        })),
      ];
    }
    return [
      partnum,
      price,
      ships,
      { key: '__name__', label: 'Product Name', sortable: true, filterable: false },
      { key: '__dimensions__', label: 'Dimensions', sortable: true, filterable: false },
      { key: '__electrical__', label: 'Electrical', sortable: true, filterable: true },
      { key: '__certifications__', label: 'Certifications', sortable: true, filterable: true },
    ];
  }, [hasSelectorColumns, selectorColumns]);

  const filterOptionsByKey = useMemo<FilterOptionsByKey>(() => {
    const options: FilterOptionsByKey = new Map();
    for (const col of columns) {
      if (col.filterable) options.set(col.key, getDistinctValues(rawChildren, col.key));
    }
    return options;
  }, [columns, rawChildren]);

  const columnWidths = useMemo(() => {
    const map: Record<string, number> = {};
    for (const col of columns) {
      map[col.key] = minWidthForColumn(col.label, col.key, rawChildren);
    }
    return map;
  }, [columns, rawChildren]);

  const tableMinWidth = useMemo(
    () => Math.max(720, columns.reduce((sum, col) => sum + columnWidths[col.key], 0)),
    [columns, columnWidths],
  );

  const columnAlignments = useMemo(() => {
    const map: Record<string, ColumnAlign> = {};
    for (const col of columns) {
      map[col.key] = getColumnAlignment(col, rawChildren);
    }
    return map;
  }, [columns, rawChildren]);

  const getFilterPopoverStyle = (anchor: HTMLElement): h.JSX.CSSProperties => {
    const container = variantsRef.current;
    const anchorRect = anchor.getBoundingClientRect();
    const containerRect = container?.getBoundingClientRect() ?? { top: 0, left: 0, width: window.innerWidth };
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const containerWidth = Math.max(220, containerRect.width);
    const width = Math.min(Math.max(anchorRect.width, 210), Math.min(300, containerWidth - 24));
    const maxLeft = Math.max(12, containerWidth - width - 12);
    const left = Math.max(12, Math.min(anchorRect.left - containerRect.left, maxLeft));
    const belowRoom = viewportHeight - anchorRect.bottom - 12;
    const aboveRoom = anchorRect.top - 12;
    let top = anchorRect.bottom - containerRect.top + 6;
    let maxHeight = Math.min(320, Math.max(150, belowRoom));

    if (belowRoom < 170 && aboveRoom > belowRoom) {
      maxHeight = Math.min(320, Math.max(150, aboveRoom));
      top = anchorRect.top - containerRect.top - maxHeight - 6;
    }

    return {
      top: `${Math.max(8, top)}px`,
      left: `${left}px`,
      minWidth: `${width}px`,
      maxHeight: `${maxHeight}px`,
    };
  };

  const refreshFilterPopover = () => {
    if (filterAnchorRef.current) {
      setFilterPopoverStyle(getFilterPopoverStyle(filterAnchorRef.current));
    }
  };

  const toggleColumnFilter = (key: string, anchor: HTMLElement) => {
    if (openFilterKey === key) {
      filterAnchorRef.current = null;
      setOpenFilterKey(null);
      setFilterPopoverStyle(undefined);
      return;
    }
    filterAnchorRef.current = anchor;
    setFilterPopoverStyle(getFilterPopoverStyle(anchor));
    setOpenFilterKey(key);
  };

  useEffect(() => {
    if (!openFilterKey) return;
    const refresh = () => refreshFilterPopover();
    const onDocMouseDown = (e: MouseEvent) => {
      const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
      if (filterPopoverRef.current && path.includes(filterPopoverRef.current)) return;
      if (filterAnchorRef.current && path.includes(filterAnchorRef.current)) return;
      suppressTableClickUntilRef.current = Date.now() + 450;
      filterAnchorRef.current = null;
      setOpenFilterKey(null);
      setFilterPopoverStyle(undefined);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        filterAnchorRef.current = null;
        setOpenFilterKey(null);
        setFilterPopoverStyle(undefined);
      }
    };
    window.requestAnimationFrame(refresh);
    window.requestAnimationFrame(() => filterPopoverRef.current?.focus());
    window.addEventListener('resize', refresh);
    window.addEventListener('scroll', refresh, true);
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      window.removeEventListener('resize', refresh);
      window.removeEventListener('scroll', refresh, true);
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [openFilterKey]);

  // Apply filters then sort. Both are pure state + data, so useMemo keeps
  // header toggles cheap.
  const visibleChildren = useMemo(() => {
    let rows = rawChildren;
    const activeFilters = Object.entries(filters);
    if (activeFilters.length > 0) {
      rows = rows.filter((child) =>
        activeFilters.every(([key, included]) => {
          if (!included) return true;
          if (included.size === 0) return false;
          return included.has(getCellValue(child, key));
        }),
      );
    }
    if (sort) {
      rows = [...rows].sort((a, b) => {
        const cmp = compareSortKeys(getCellSortKey(a, sort.key), getCellSortKey(b, sort.key));
        return sort.dir === 'asc' ? cmp : -cmp;
      });
    }
    return rows;
  }, [rawChildren, filters, sort]);

  const toggleSort = (key: string) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return { key, dir: 'asc' };
    });
  };

  const setFilter = (key: string, included: Set<string> | null) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (included == null) delete next[key];
      else next[key] = included;
      return next;
    });
  };

  const activeFilterColumn = openFilterKey ? columns.find((col) => col.key === openFilterKey) : undefined;
  const activeFilterValues = openFilterKey ? (filterOptionsByKey.get(openFilterKey) ?? []) : [];
  const activeFilter = openFilterKey ? filters[openFilterKey] : undefined;
  const isActiveFilterModified = !!activeFilter && activeFilterValues.length > 0 && activeFilter.size < activeFilterValues.length;
  const toggleFilterValue = (value: string) => {
    if (!openFilterKey) return;
    const current = activeFilter ?? new Set(activeFilterValues);
    const next = new Set(current);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setFilter(openFilterKey, next.size === activeFilterValues.length ? null : next);
    window.requestAnimationFrame(refreshFilterPopover);
  };
  const resetActiveFilter = () => {
    if (!openFilterKey) return;
    setFilter(openFilterKey, null);
    window.requestAnimationFrame(refreshFilterPopover);
  };

  const renderLegacyCell = (child: FamilyChild, key: string): h.JSX.Element | string => {
    if (key === '__name__') return cleanAttribute(child.name);
    if (key === '__dimensions__') return cleanAttribute(child.dimensions);
    if (key === '__electrical__') return cleanAttribute(child.electrical_specs);
    if (key === '__certifications__') return formatCertifications(child);
    return '-';
  };

  return (
    <div class={`ts-family-stack ${expanded ? 'ts-family-stack--expanded' : 'ts-family-stack--collapsed'}`}>
      <ResultCard
        product={product}
        query={query}
        position={position}
        viewMode="list"
      />

      {variantCount > 0 && (
        <button
          class="ts-family-tab"
          onClick={() => {
            setExpanded(!expanded);
            filterAnchorRef.current = null;
            setOpenFilterKey(null);
            setFilterPopoverStyle(undefined);
          }}
          aria-expanded={expanded ? 'true' : 'false'}
          aria-label={`${expanded ? 'Hide' : 'Show'} ${variantCount} models`}
        >
          <span class="ts-family-tab-text">
            <span class="ts-family-tab-word">{expanded ? 'Hide' : 'Show all'}</span>
            <span class="ts-family-tab-count">{variantCount}</span>
            <span class="ts-family-tab-word">models</span>
          </span>
          <span class="ts-family-tab-chevron" aria-hidden="true" />
        </button>
      )}

      {expanded && (
        <div
          class="ts-family-variants"
          ref={variantsRef}
          onClickCapture={(e) => {
            if (Date.now() >= suppressTableClickUntilRef.current) return;
            e.preventDefault();
            e.stopPropagation();
            suppressTableClickUntilRef.current = 0;
          }}
        >
          {loading ? (
            <div class="ts-family-variant-skeleton">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} class="ts-family-variant-skeleton-row">
                  <div class="ts-skeleton ts-skeleton--text ts-skeleton--w40" />
                  <div class="ts-skeleton ts-skeleton--text ts-skeleton--w80" />
                  <div class="ts-skeleton ts-skeleton--text ts-skeleton--w60" />
                </div>
              ))}
            </div>
          ) : rawChildren.length > 0 ? (
            <>
              <div class="ts-family-variant-table-wrap">
                <table class="ts-family-variant-table" style={{ minWidth: `${tableMinWidth}px` }}>
                  <colgroup>
                    {columns.map((col) => (
                      <col
                        key={col.key}
                        class={getColumnWidthClass(col)}
                        style={{ width: `${columnWidths[col.key]}px` }}
                      />
                    ))}
                  </colgroup>
                  <thead>
                    <tr>
                      {columns.map((col) => (
                        <ColumnHeader
                          key={col.key}
                          column={col}
                          filterValues={filterOptionsByKey.get(col.key) ?? []}
                          sort={sort}
                          toggleSort={toggleSort}
                          filter={filters[col.key]}
                          openFilterKey={openFilterKey}
                          onToggleFilter={toggleColumnFilter}
                          minWidth={columnWidths[col.key]}
                          align={columnAlignments[col.key]}
                        />
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleChildren.length === 0 ? (
                      <tr>
                        <td colSpan={columns.length} class="ts-family-variant-empty-row">
                          No models match the current filters.
                          <button type="button" class="ts-family-variants-reset" onClick={() => setFilters({})}>
                            Reset filters
                          </button>
                        </td>
                      </tr>
                    ) : (
                      visibleChildren.map((child) => {
                        const priceLabel =
                          child.price != null && child.price > 0 ? formatPrice(child.price) : '-';
                        // Whole-row navigation: click anywhere → open the
                        // product page.  Anchors / buttons inside cells
                        // (the Part # link, popovers in the head) are
                        // detected via closest() and allowed to handle
                        // their own clicks.  Keyboard parity via
                        // Enter / Space.  role="link" + aria-label so
                        // screen readers announce the row as a single
                        // navigation target.
                        const navigate = () => {
                          if (child.product_url) {
                            trackClick(query, child.product_id, position, {
                              search_event_id: searchEventId,
                              part_number: child.part_number,
                              product_name: child.name,
                              vendor: product.vendor,
                              surface: 'family_table',
                              result_type: 'child',
                              product_url: child.product_url,
                              price: child.price,
                            });
                            window.open(child.product_url, '_blank', 'noopener,noreferrer');
                          }
                        };
                        const onRowClick = (e: MouseEvent) => {
                          const target = e.target as HTMLElement;
                          if (target.closest('a, button, input, label')) return;
                          navigate();
                        };
                        const onRowKey = (e: KeyboardEvent) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate();
                          }
                        };
                        return (
                          <tr
                            key={child.product_id}
                            class="ts-family-variant-row"
                            onClick={onRowClick}
                            onKeyDown={onRowKey}
                            tabIndex={child.product_url ? 0 : -1}
                            role={child.product_url ? 'link' : undefined}
                            aria-label={child.product_url ? `Open ${child.name}` : undefined}
                          >
                            {columns.map((col) => {
                              if (col.key === COL_PARTNUM) {
                                return (
                                  <td key={col.key} class={`ts-family-variant-cell--partnum ts-family-cell--align-${columnAlignments[col.key]}`}>
                                    <a
                                      href={child.product_url}
                                      class="ts-family-variant-part"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={() => {
                                        trackClick(query, child.product_id, position, {
                                          search_event_id: searchEventId,
                                          part_number: child.part_number,
                                          product_name: child.name,
                                          vendor: product.vendor,
                                          surface: 'family_table',
                                          result_type: 'child',
                                          product_url: child.product_url,
                                          price: child.price,
                                        });
                                      }}
                                    >
                                      {child.part_number || 'Open model'}
                                    </a>
                                  </td>
                                );
                              }
                              if (col.key === COL_PRICE) {
                                return (
                                  <td key={col.key} class={`ts-family-variant-price-col ts-family-variant-cell--price ts-family-cell--align-${columnAlignments[col.key]}`}>
                                    <span class="ts-family-variant-price">{priceLabel}</span>
                                  </td>
                                );
                              }
                              if (col.key === COL_SHIPS) {
                                return (
                                  <td key={col.key} class={`ts-family-variant-cell--ships ts-family-cell--align-${columnAlignments[col.key]}`}>
                                    <span class={isChildReadyNow(child) ? 'ts-family-variant-ship ts-family-variant-ship--stock' : 'ts-family-variant-ship'}>
                                      {formatLeadTime(child)}
                                    </span>
                                  </td>
                                );
                              }
                              if (hasSelectorColumns) {
                                return (
                                  <td key={col.key} class={`ts-family-variant-cell--attr ts-family-cell--align-${columnAlignments[col.key]}`}>
                                    {cleanAttribute(child.selector_values?.[col.key])}
                                  </td>
                                );
                              }
                              return (
                                <td key={col.key} class={`ts-family-variant-cell--attr ts-family-cell--align-${columnAlignments[col.key]}`}>
                                  {renderLegacyCell(child, col.key)}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              {activeFilterColumn && activeFilterValues.length > 1 && (
                <div
                  class="ts-family-th-popover"
                  ref={filterPopoverRef}
                  role="dialog"
                  tabIndex={-1}
                  aria-label={`Filter ${activeFilterColumn.label}`}
                  style={filterPopoverStyle || { visibility: 'hidden' }}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div class="ts-family-th-popover-head">
                    <span class="ts-family-th-popover-title">{activeFilterColumn.label}</span>
                    {isActiveFilterModified && (
                      <button type="button" class="ts-family-th-popover-link" onClick={resetActiveFilter}>
                        Reset
                      </button>
                    )}
                  </div>
                  <div class="ts-family-th-popover-list">
                    {activeFilterValues.map((value) => {
                      const checked = activeFilter ? activeFilter.has(value) : true;
                      return (
                        <label key={value} class="ts-family-th-popover-row">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleFilterValue(value)}
                          />
                          <span>{value}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div class="ts-family-carousel-empty">No models available</div>
          )}
        </div>
      )}
    </div>
  );
}
